import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, currency, SpinningLoader, Text, useTooltip } from '@frontend/design-system';
import { useCareCreditTranslations } from '../hooks/use-care-credit-translations';
import { CareCreditPatientData } from '../types/care-credit.types';
import { CareCreditChip } from './care-credit-chip';

const styles = {
  item: css`
    position: relative;
    padding: var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-md);
    background: var(--fabric-color-neutral-00);
    box-shadow: ${theme.shadows.light};
  `,
  topRow: css`
    display: flex;
    gap: var(--fabric-space-gap-md);
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--fabric-space-gap-xs);
  `,
  brandLine: css`
    display: flex;
    align-items: center;
    line-height: 1;
    gap: var(--fabric-space-gap-xs);
    font-size: var(--fabric-font-size-lg);
  `,
  loadingContainer: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
  `,
  spinner: css`
    opacity: 0.5;
  `,
  subText: css`
    color: var(--fabric-color-neutral-50);
    font-weight: 400;
  `,
  preApprovalText: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
  `,
  unknownStatusBox: css`
    background: var(--fabric-color-neutral-5);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-md);
    gap: var(--fabric-space-gap-xs);
    color: var(--fabric-color-neutral-70);

    svg {
      color: var(--fabric-color-neutral-60);
    }
  `,
};

export type CareCreditPersonStatusItemProps = {
  className?: string;
  careCreditData?: CareCreditPatientData;
  isLoading?: boolean;
  hasValidPhoneNumber?: boolean;
  onClickPrequalify?: () => void;
  onClickPreApproved?: () => void;
};
export const CareCreditPersonStatusItem = ({
  careCreditData,
  className,
  isLoading,
  hasValidPhoneNumber,
  onClickPrequalify,
  onClickPreApproved,
}: CareCreditPersonStatusItemProps) => {
  const { t } = useCareCreditTranslations();

  const { status, amount, asOfDate } = careCreditData || {};

  let bottomRowJsx: React.ReactNode = null;
  let chipJsx: React.ReactNode = null;

  const { Tooltip, triggerProps, tooltipProps } = useTooltip({
    placement: 'left',
  });

  const formattedAmount = amount ? currency(amount, { decimals: 0 }) : 'N/A';
  const date = asOfDate ? dayjs(asOfDate).format('MMM DD, YYYY') : null;

  if (!isLoading) {
    if (!careCreditData) {
      bottomRowJsx = (
        <div css={styles.unknownStatusBox}>
          <div>
            <Icon name='help' size={16} />
          </div>
          <div>
            <Text size='medium'>{t('Unable to retrieve CareCredit eligibility status at this time')}</Text>
          </div>
        </div>
      );
    } else if (status === 'approved') {
      bottomRowJsx = (
        <Text size='small'>
          <Trans>
            Available: <b>{formattedAmount}</b> as of {date}
          </Trans>
        </Text>
      );
    } else if (status === 'pre-approved') {
      bottomRowJsx = (
        <div css={styles.preApprovalText}>
          <div>
            {hasValidPhoneNumber === false ? (
              <>
                <Button variant='secondary' {...triggerProps} disabled={true}>
                  {t('Send Approval Link')}
                </Button>
                <Tooltip {...tooltipProps}>{t('No valid phone number')}</Tooltip>
              </>
            ) : (
              <Button variant='secondary' onClick={onClickPreApproved} disabled={!onClickPreApproved}>
                {t('Send Approval Link')}
              </Button>
            )}
          </div>
          <div>
            <Text size='small'>
              <i css={styles.subText}>{t('Customer can complete approval and pay after.')}</i>
            </Text>
          </div>
        </div>
      );
    } else if (status === 'not-enrolled') {
      bottomRowJsx = (
        <Button variant='secondary' onClick={onClickPrequalify} disabled={!onClickPrequalify}>
          {t('Check Now')}
        </Button>
      );
    }

    if (status) {
      chipJsx = <CareCreditChip status={status} />;
    }
  }

  return (
    <div className={`care-credit-person-status-item ${className || ''}`} css={styles.item}>
      <div className='top-row' css={styles.topRow}>
        <div>
          <Text size='medium' weight='bold' css={styles.brandLine}>
            <Icon name='care-credit' height={16} width={16} />
            {t('CareCredit')}
          </Text>
        </div>
        <div>{chipJsx}</div>
      </div>

      <div className='bottom-row'>{bottomRowJsx}</div>

      {isLoading && (
        <div css={styles.loadingContainer}>
          <SpinningLoader size='xs' css={styles.spinner} />
        </div>
      )}
    </div>
  );
};
